import { AREAS, fmtYmd, getMaxSelectableDate } from '~/constants/spotPrice'
import { now as serverNow } from '~/models/clock'
import { parseLocalYmd, startOfTodayJst } from '~/models/dateTime'
import {
  type FilterCodec,
  INVALID,
  calendarDateString,
  defineFilterCodec,
  field,
  oneOf,
  setOf,
} from '~/models/filterCodec'
import { limitPeriodEnd } from '~/pages/SpotPrice/models/spotPricePeriod'

/**
 * スポット価格のフィルタバー state (in-memory 形状)。画面遷移・再訪をまたいで
 * 保持する対象 (AC-SC-031 [P0])。
 *
 * この画面はアコーディオン見出しから供給力変化画面へ route 遷移するため、
 * 保持しないと戻るたびに期間とエリア選択が初期値へリセットされる。
 *
 * ## 保持しないもの
 *
 * - **コマ別表のページャ位置** (`komaPageDate`) — フィルタではなく `dateFrom` /
 *   `dateTo` の範囲内へ clamp される派生位置。期間を復元すればページャは
 *   その先頭から始まればよい。
 * - **複数日判定** (`isMultiDay`) — `dateFrom` / `dateTo` からの純粋な派生値。
 *   保存すると 2 つの真実ができる。
 *
 * ## スプレッド対を保持しないのは「まだ無い」から
 *
 * design `spot-price.html` は表示するスプレッド対を localStorage
 * (`jepxSpotSpreadPair`) に持つが、React 実装は `components/StatsTable.tsx` で
 * 東京 − 関西の決め打ちで、**利用者が選ぶ state 自体が存在しない**。選択 UI が
 * 入った時に保持対象へ加える。
 */
export type SpotPriceFilters = {
  /** 期間の開始日 (`YYYY-MM-DD`)。 */
  dateFrom: string
  /** 期間の終了日 (`YYYY-MM-DD`)。 */
  dateTo: string
  /** 表示中の系列 (エリア) コード。 */
  visibleAreas: Set<string>
}

/**
 * フィルタ既定値。期間は「JST の今日」の単日、系列は全 ON (design L228-230)。
 */
export function defaultSpotPriceFilters(
  now: Date = serverNow(),
): SpotPriceFilters {
  const today = fmtYmd(startOfTodayJst(now))
  return {
    dateFrom: today,
    dateTo: today,
    visibleAreas: new Set(AREAS.map((a) => a.code)),
  }
}

const ymd = calendarDateString({ parse: parseLocalYmd, format: fmtYmd })

/**
 * 上限日を超える保存値は上限日へ clamp する。 `max` 属性は**利用者の新しい入力**しか
 * 止めず、復元した controlled value には効かない。 保存済みの未来日がそのまま一覧
 * 取得と供給力変化画面への遷移に使われる (review 指摘 #6932)。
 * 上限は復元に注入された `now` から導くので決定的。
 */
const clampedYmd = field<string>(
  (wire, ctx) => {
    const decoded = ymd.decode(wire, ctx)
    if (decoded === INVALID) return INVALID
    const parsed = parseLocalYmd(decoded)
    const maxDate = getMaxSelectableDate(ctx.now)
    return parsed !== null && parsed.getTime() > maxDate.getTime()
      ? fmtYmd(maxDate)
      : decoded
  },
  (value) => value,
)

/**
 * 保存形式は `{ dateFrom, dateTo: 'YYYY-MM-DD', visibleAreas: string[] }`。壊れた値・
 * 未知のエリアコードは項目単位で既定へ落ちる。
 *
 * **期間の前後関係は直さない。** `dateFrom > dateTo` は UI 上も作れる状態で、
 * `isMultiDay` が false になるだけ (単日扱い)。復元時に入れ替えると「保存した状態と
 * 違うものが出る」。
 */
const baseSpotPriceFiltersCodec = defineFilterCodec<SpotPriceFilters>({
  defaults: defaultSpotPriceFilters,
  fields: {
    dateFrom: clampedYmd,
    dateTo: clampedYmd,
    visibleAreas: setOf(oneOf<string>(AREAS.map((a) => a.code))),
  },
})

/**
 * 復元した終了日も、 開始日から 31 日以内に寄せる (#1682)。 以前の画面で 31 日を超える
 * 期間を保存していても、 期間欄・取得範囲・コマ別表のページャの範囲が食い違わない。
 * 項目をまたぐ補正なので、 項目ごとの codec ではなくここで通す。
 */
export const spotPriceFiltersCodec: FilterCodec<SpotPriceFilters> = {
  serialize: baseSpotPriceFiltersCodec.serialize,
  deserialize: (raw, now) => {
    const restored = baseSpotPriceFiltersCodec.deserialize(raw, now)
    return {
      ...restored,
      dateTo: limitPeriodEnd(restored.dateFrom, restored.dateTo),
    }
  },
}
